import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Grid, CircularProgress } from "@mui/material";
import api from "../../globals/axios";
import { SERVER_BASE_ADDRESS } from "../../globals/const";
import CustomAppBar from "../../components/AppBar";
import "../../styles/FarmOverviewStyle.css";

import StatsSection from "../../components/Admin/StatsSection";
import FarmsListSection from "../../components/Admin/FarmsListSection";
import MapComponent from "../../components/Admin/MapComponent";

const LoadingIndicator = ({ size = 80 }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "90%",
      height: "90%",
    }}
  >
    <CircularProgress size={size} />
  </Box>
);

const FarmsMapSummary = () => {
  const navigate = useNavigate();

  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [mapZoom, setMapZoom] = useState(6);
  const [stats, setStats] = useState({
    totalFarmers: 0,
    totalArea: 0,
    totalFarms: 0,
  });
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [clickedMarker, setClickedMarker] = useState(null);

  // Helper functions
  const extractCoordinates = (polygonStr) => {
    try {
      const coordsMatch = polygonStr.match(/SRID=\d+;POLYGON \(\((.*?)\)\)/);
      if (!coordsMatch || !coordsMatch[1]) return null;

      const coordinates = coordsMatch[1];
      const firstCoord = coordinates.split(",")[0].trim().split(" ");

      return {
        lng: parseFloat(firstCoord[0]),
        lat: parseFloat(firstCoord[1]),
      };
    } catch (error) {
      console.error("Error parsing polygon:", error);
      return null;
    }
  };

  const handleCardClick = (farm) => {
    setSelectedFarm(farm);
    const coords = extractCoordinates(farm.polygon);
    if (coords) {
      setMapCenter(coords);
      setMapZoom(18);
    }
  };

  const handleLocationIconClick = (e, farm) => {
    e.stopPropagation();
    const coords = extractCoordinates(farm.polygon);
    if (coords) {
      setSelectedFarm(farm);
      setMapCenter(coords);
      setMapZoom(18);
    }
  };

  const handleMarkerClick = (farm) => {
    let farmerId = null;

    if (farm.farmer) {
      if (typeof farm.farmer === "string" && farm.farmer.includes(":")) {
        farmerId = farm.farmer.split(":")[1];
      } else if (typeof farm.farmer === "object" && farm.farmer.id) {
        farmerId = farm.farmer.id;
      } else if (typeof farm.farmer === "number") {
        farmerId = farm.farmer;
      }
    }

    if (farmerId) {
      navigate(`/farmer-details/${farmerId}`);
    } else {
      setSelectedFarm(farm);
      setClickedMarker(farm);
      const coords = extractCoordinates(farm.polygon);
      if (coords) {
        setMapCenter(coords);
        setMapZoom(18);
      }
    }
  };

  useEffect(() => {
    const fetchFarms = async () => {
      try {
        const response = await api.get(
          `${SERVER_BASE_ADDRESS}/app-admin/get-farmer-farms/`
        );

        const farmsData = response.data.farms || response.data;
        const totalFarms = response.data.total_farms || farmsData.length;
        const totalArea = response.data.total_area || 0;

        setFarms(farmsData);

        const uniqueFarmerIds = new Set();
        farmsData.forEach((farm) => {
          if (farm.farmer) {
            let farmerId;
            if (typeof farm.farmer === "string" && farm.farmer.includes(":")) {
              farmerId = farm.farmer.split(":")[1];
            } else if (typeof farm.farmer === "object" && farm.farmer.id) {
              farmerId = farm.farmer.id;
            } else if (typeof farm.farmer === "number") {
              farmerId = farm.farmer;
            }
            if (farmerId) uniqueFarmerIds.add(farmerId);
          }
        });

        setStats({
          totalFarmers: uniqueFarmerIds.size,
          totalArea: totalArea,
          totalFarms: totalFarms,
        });

        if (farmsData.length > 0) {
          const firstFarmCoords = extractCoordinates(farmsData[0].polygon);
          if (firstFarmCoords) {
            setMapCenter(firstFarmCoords);
          }
        }
      } catch (error) {
        console.error("Error fetching farm data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFarms();
  }, []);

  return (
    <Box
      sx={{
        height: "92vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CustomAppBar />
      <Box
        sx={{
          p: 3,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <StatsSection stats={stats} />

        <Paper
          elevation={3}
          sx={{
            p: 2,
            flexGrow: 1,
            display: "flex",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          {loading ? (
            <LoadingIndicator />
          ) : (
            <Grid
              container
              spacing={3}
              sx={{ flexGrow: 1, overflow: "hidden", height: "100%" }}
            >
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <FarmsListSection
                  farms={farms}
                  selectedFarm={selectedFarm}
                  handleCardClick={handleCardClick}
                  handleLocationIconClick={handleLocationIconClick}
                />
              </Grid>
              <Grid item xs={12} md={8} sx={{ height: "100%" }}>
                <MapComponent
                  farms={farms}
                  mapCenter={mapCenter}
                  mapZoom={mapZoom}
                  selectedFarm={selectedFarm}
                  hoveredMarker={hoveredMarker}
                  clickedMarker={clickedMarker}
                  setHoveredMarker={setHoveredMarker}
                  setClickedMarker={setClickedMarker}
                  handleMarkerClick={handleMarkerClick}
                  extractCoordinates={extractCoordinates}
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default FarmsMapSummary;
